import { HttpInterceptorFn } from '@angular/common/http';
import { inject, NgZone } from '@angular/core';
import { Observable } from 'rxjs';

/**
 * ngZoneReentryInterceptor — re-delivers every HTTP notification (next/error/
 * complete) inside NgZone, no matter which zone the response actually arrived
 * on (#1811).
 *
 * Root cause: `authHttpInterceptorFn` (auth0-angular) calls into auth0-spa-js's
 * `getTokenSilently()` before forwarding the request. That call can go through
 * `navigator.locks` (WebLocksApiManager, node_modules/@auth0/auth0-spa-js/src/lock.ts)
 * and/or a silent-SSO iframe round-trip — browser APIs zone.js does not patch.
 * When that path is taken (a token refresh), the continuation escapes NgZone,
 * so the real XHR is issued from the root zone and every downstream rxjs
 * callback (tap/catchError/finalize in feature components) runs OUTSIDE
 * Angular. Plain-field writes in those callbacks never schedule change
 * detection — the component's state is correct but the view is never told to
 * re-render (confirmed live via `ng.applyChanges()` instantly painting the
 * correct state — see issue #1811). Signal-writing surfaces are unaffected
 * because Angular's v21 hybrid scheduler ticks on signal writes regardless of
 * zone; only zone-based (plain-field) components hang.
 *
 * Fix: wrap `next(req)` in a new Observable that always forwards emissions via
 * `NgZone.run()`. This heals every consumer of HttpClient in one shared place
 * instead of requiring each component to migrate to signals or manually
 * re-enter the zone.
 *
 * Register this FIRST (outermost) in `withInterceptors([...])` — responses
 * bubbling back up through `authHttpInterceptorFn` and any other interceptor
 * pass through this wrapper LAST, right before they reach application code.
 */
export const ngZoneReentryInterceptor: HttpInterceptorFn = (req, next) => {
  const zone = inject(NgZone);

  return new Observable(subscriber => {
    const inner = next(req).subscribe({
      next: value => {
        if (NgZone.isInAngularZone()) {
          subscriber.next(value);
        } else {
          zone.run(() => subscriber.next(value));
        }
      },
      error: error => {
        if (NgZone.isInAngularZone()) {
          subscriber.error(error);
        } else {
          zone.run(() => subscriber.error(error));
        }
      },
      complete: () => {
        if (NgZone.isInAngularZone()) {
          subscriber.complete();
        } else {
          zone.run(() => subscriber.complete());
        }
      },
    });

    return () => inner.unsubscribe();
  });
};
